import { makeAutoObservable } from "mobx"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { GapDataGridProps } from "./GapDataGrid"
import GapDataGridHeaderTypography from "./GapDataGridHeaderTypography"
import { notNull } from "@gap/utils/utils"
import { GridApi, gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid'
import { GridRowSelectionModel, GridValidRowModel } from "@mui/x-data-grid"
import GapDataGridSkeletonCell from "./GapDataGridSkeletonCell"

export default class GapDataGridStore<T extends GridValidRowModel> {
   sp
   selectedIds: string[] = []
   noRowsTextForTab: undefined | string = undefined
   archiveMode = false

   constructor(sp: GapDataGridProps<T> & { gridApiRef: React.MutableRefObject<GridApi | null> }) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get tabs() {
      return this.sp.toolbar?.tabs?.map(tab => ({
         label: tab.label,
         noRowsText: tab.noRowsText,
         items: tab.filters.map((filter, i) => ({
            ...filter,
            id: filter.id || `${tab.label}-${i}`
         }))
      }))
   }

   get columns() {
      return this.sp.columns.map(column => {
         if (!column.renderHeader && column.headerName) {
            column.renderHeader = () => <GapDataGridHeaderTypography title={notNull(column.headerName, 'Ensure the header column has the headerName setup')} />
         }
         if (!column.flex) {
            column.flex = 1
         }
         if (!column.minWidth) {
            column.minWidth = 150
         }
         column.renderCell = column.renderCell ? GapDataGridSkeletonCell(column.renderCell) : GapDataGridSkeletonCell()
         return column
      })
   }

   get selectedCount() {
      return this.selectedIds.length
   }

   /** Rows passed to the grid after applying archive mode. Archive filtering is done here (on the array) so it stays orthogonal to the MUI filter model used for tabs and search. */
   get displayRows() {
      const archive = this.sp.toolbar?.archive
      if (!archive) return this.sp.rows
      return this.sp.rows.filter(row => this.archiveMode ? archive.isArchived(row) : !archive.isArchived(row))
   }

   get noRowsText() {
      return this.noRowsTextForTab || this.sp.noRowsText || 'No results'
   }

   toggleArchiveMode = () => {
      this.archiveMode = !this.archiveMode
      this.selectedIds = []
      this.sp.gridApiRef.current?.setRowSelectionModel({ type: 'include', ids: new Set() })
   }

   /** 'exclude' lists the rows that are NOT selected, so select-all in the header arrives as an empty set. */
   onRowSelectionModelChange = (selectionModel: GridRowSelectionModel) => {
      if (selectionModel.type === 'include') {
         this.selectedIds = Array.from(selectionModel.ids).map(String)
         return
      }
      if (!this.sp.gridApiRef.current) {
         this.selectedIds = []
         return
      }
      this.selectedIds = gridFilteredSortedRowIdsSelector(this.sp.gridApiRef as React.RefObject<GridApi>)
         .filter(rowId => !selectionModel.ids.has(rowId))
         .map(String)
   }
}

export const { Provider: GapDataGridStoreProvider, useProvidedStore: useGapDataGridStore } = createStoreContext(GapDataGridStore<any>)
